import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, type ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { isValidTime, todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useParams } from '../../../navigation/nav';
import { Button, Card, Columns, DataTable, DateField, Grid, PageHeader, Screen, Stack, StatusBadge, Text, TextField } from '../../../ui';
import { WorkflowSheet } from '../components/WorkflowSheet';

export function RegulariseScreen() {
  const params = useParams<{ date: string }>();
  const [form, setForm] = useState({ date: params.date ?? todayISO(), in_time: '', out_time: '', reason: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [wf, setWf] = useState<ID | null>(null);
  useEffect(() => {
    if (params.date) setForm((f) => ({ ...f, date: params.date! }));
  }, [params.date]);
  const list = useQuery({ queryKey: ['regularisations', 'mine', page], queryFn: () => api.attendance.regularisations({ scope: 'mine', page }) });
  const submit = useMut(() => api.attendance.regularise({ ...form, in_time: form.in_time || undefined, out_time: form.out_time || undefined }), {
    invalidate: [['regularisations'], ['attendance']],
    success: 'Regularisation submitted',
    onSuccess: () => {
      setForm({ ...form, in_time: '', out_time: '', reason: '' });
      setErrs({});
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const timeErr = (t: string) => (t && !isValidTime(t) ? 'Use 24h HH:mm, e.g. 09:30' : undefined);
  const invalid = !form.reason.trim() || !!timeErr(form.in_time) || !!timeErr(form.out_time);
  return (
    <Screen>
      <PageHeader title="Regularise attendance" subtitle="Request a correction for a missed or incorrect punch." />
      <Columns ratio={[2, 3]}>
        <Card title="New request">
          <Stack gap={14}>
            <DateField label="Date" required value={form.date} max={todayISO()} onChange={(date) => setForm({ ...form, date })} error={errs.date} />
            <Grid min={140} gap={12}>
              <TextField label="In time" placeholder="09:30" value={form.in_time} onChangeText={(in_time) => setForm({ ...form, in_time })} error={errs.in_time ?? timeErr(form.in_time)} maxLength={5} />
              <TextField label="Out time" placeholder="18:30" value={form.out_time} onChangeText={(out_time) => setForm({ ...form, out_time })} error={errs.out_time ?? timeErr(form.out_time)} maxLength={5} />
            </Grid>
            <TextField label="Reason" required multiline placeholder="e.g. Forgot to punch out, client visit" value={form.reason} onChangeText={(reason) => setForm({ ...form, reason })} error={errs.reason} />
            <View style={{ alignItems: 'flex-end' }}>
              <Button title="Submit" variant="primary" loading={submit.isPending} disabled={invalid} onPress={() => submit.mutate(undefined)} />
            </View>
          </Stack>
        </Card>
        <Stack gap={10}>
          <Text variant="title">My requests</Text>
          <DataTable
            rows={list.data}
            loading={list.isLoading}
            onPageChange={setPage}
            onRowPress={(r) => r.workflow_instance_id && setWf(r.workflow_instance_id)}
            emptyTitle="No regularisation requests"
            columns={[
              { key: 'date', title: 'Date', width: 110, render: (r) => fmtDate(r.date), sortValue: (r) => r.date },
              { key: 'in_time', title: 'In', width: 70 },
              { key: 'out_time', title: 'Out', width: 70 },
              { key: 'reason', title: 'Reason', width: 180, flex: 2 },
              { key: 'status', title: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} />, sortValue: (r) => r.status },
            ]}
          />
        </Stack>
      </Columns>
      <WorkflowSheet instanceId={wf} onClose={() => setWf(null)} />
    </Screen>
  );
}
